import { LEAD_FORM_NAME } from '@/features/lead-form/constants/formName';
import { LEAD_TYPE } from '@/features/lead-form/constants/leadType';
import type { ProductType } from '@/features/lead-form/types/leadData';
import type { ReactNode } from 'react';

export type PdfDownloadCopy = {
  popupTitle: string;
  formHeader: (name: string) => ReactNode;
  submitLabel?: string;
  submitIcon?: boolean;
  completeTitle: (name: string) => string;
  completeDesc: string;
  completeNote?: string;
  completeButtonLabel?: string;
};

// ─────────────────────────────────────────────────────────
// 팝업 설정 (카피 + 폼 설정)
// ─────────────────────────────────────────────────────────

/** 상품/지점별 팝업 설정 */
export const PDF_DOWNLOAD_CONFIG = {
  product: {
    copy: {
      popupTitle: '상품 소개 자료 다운로드',
      formHeader: (name: string) => <>{name} 상품 소개 자료를 <br className="hidden_pc"/>확인해 보세요</>,
      completeTitle: (name: string) => `${name} 소개 자료 다운로드 완료`,
      completeDesc: '담당 매니저가 상품 안내를 위해 연락 드리겠습니다.',
    },
    form: {
      formName: LEAD_FORM_NAME.PRODUCT_BROCHURE_DOWNLOAD,
    },
  },
  branch: {
    copy: {
      popupTitle: '지점 소개 자료 다운로드',
      formHeader: (name: string) => `${name} 소개 자료를 확인해 보세요`,
      submitLabel: '지점 소개 자료 무료 다운로드',
      submitIcon: true,
      completeTitle: (name: string) => `${name} 소개 자료 다운로드 완료`,
      completeDesc: '담당 매니저가 지점 안내를 위해 연락 드리겠습니다.',
      completeButtonLabel: '확인하기',
    },
    form: {
      formName: LEAD_FORM_NAME.BRANCH_BROCHURE_DOWNLOAD,
      leadType: LEAD_TYPE.SHARED_OFFICE,
      productType: '공유오피스' as ProductType,
    },
  },
} as const;

// ─────────────────────────────────────────────────────────
// PDF 파일 데이터
// ─────────────────────────────────────────────────────────

const PDF_BASE_URL = 'https://assets.fastfive.co.kr/pdf';

/**
 * 정적 상품 소개서 URL. 지점 소개서는 지점 API(/branches/{branchId}/files)에서 조회
 * 저장 파일명은 `${상품명} 소개서.pdf`로 자동 생성
 */
export const PDF_DOWNLOAD_FILES = {
  공유오피스: `${PDF_BASE_URL}/product/shared_office_profile.pdf`,
  전용오피스: `${PDF_BASE_URL}/product/private_office_profile.pdf`,
  맞춤형오피스: `${PDF_BASE_URL}/product/custom_office_profile.pdf`,
} as const;

// ─────────────────────────────────────────────────────────
// 타입 (데이터에서 파생)
// ─────────────────────────────────────────────────────────

export type PdfDownloadCategory = keyof typeof PDF_DOWNLOAD_CONFIG;
export type PdfDownloadProduct = keyof typeof PDF_DOWNLOAD_FILES;

/** 상품명은 자동완성을 제공하고, API에서 조회할 지점명은 문자열로 받는다. */
export type PdfDownloadName = PdfDownloadProduct | (string & {});
